import { logger } from '~/utils/logger';
import { defineStore } from "pinia";
import type { AdminNotificationPayload } from "~/types/socket-events";

export interface Notification {
  id: string;
  type: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  status: "UNREAD" | "READ" | "ARCHIVED";
  title: string;
  message: string;
  userId?: string;
  relatedOrderId?: string;
  relatedBookingId?: string;
  relatedInventoryId?: string;
  relatedTravelerId?: string;
  relatedDocumentId?: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const useNotificationStore = defineStore("notifications", {
  state: () => ({
    notifications: [] as Notification[],
    loading: false,
    initialized: false,
  }),

  getters: {
    unreadNotifications: (state) =>
      state.notifications.filter((n) => n.status === "UNREAD"),

    readNotifications: (state) =>
      state.notifications.filter((n) => n.status === "READ"),

    urgentNotifications: (state) =>
      state.notifications.filter(
        (n) => n.priority === "URGENT" && n.status === "UNREAD"
      ),

    // Calculate unreadCount from actual notifications array to avoid sync issues
    unreadCount: (state) =>
      state.notifications.filter((n) => n.status === "UNREAD").length,

    hasUnread: (state) =>
      state.notifications.filter((n) => n.status === "UNREAD").length > 0,

    sortedNotifications: (state) =>
      [...state.notifications].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
  },

  actions: {
    /**
     * Initialize notifications store
     */
    async initialize() {
      if (this.initialized) return;

      await this.fetchNotifications();
      this.initialized = true;
    },

    /**
     * Fetch notifications from API
     */
    async fetchNotifications(status?: "UNREAD" | "READ" | "ARCHIVED") {
      try {
        this.loading = true;
        const query = status ? `?status=${status}` : "";
        const data = await $fetch<Notification[]>(`/api/notifications${query}`);

        // Ensure all notifications have valid date strings
        this.notifications = data.map((notif) => ({
          ...notif,
          createdAt: notif.createdAt || new Date().toISOString(),
          updatedAt: notif.updatedAt || new Date().toISOString(),
          readAt: notif.readAt || undefined,
        }));
      } catch (error) {
        logger.error("Error fetching notifications:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Add a new notification (from WebSocket)
     */
    addNotification(notification: Notification) {
      logger.log(
        "[Notification] Adding notification:",
        notification.id,
        notification.title
      );

      // Check if notification already exists
      const exists = this.notifications.find((n) => n.id === notification.id);
      if (exists) {
        logger.log("[Notification] Notification already exists, skipping");
        return;
      }

      this.notifications.unshift(notification);
      // unreadCount is now calculated dynamically from getter

      logger.log(
        "[Notification] About to play sound and show browser notification"
      );

      // Play notification sound
      this.playNotificationSound();

      // Show browser notification if supported
      this.showBrowserNotification(notification);
    },

    /**
     * Add admin notification from WebSocket
     */
    addAdminNotification(payload: AdminNotificationPayload) {
      // Check if notification already exists (prevent duplicates from socket)
      const exists = this.notifications.find((n) => n.id === payload.id);
      if (exists) {
        logger.log(
          "[Notification] Duplicate notification ignored:",
          payload.id
        );
        return;
      }

      const notification: Notification = {
        id: payload.id,
        type: payload.type,
        priority: payload.priority,
        status: "UNREAD",
        title: payload.title,
        message: payload.message,
        actionUrl: payload.actionUrl,
        relatedOrderId: payload.relatedOrderId || payload.relatedId,
        relatedBookingId: payload.relatedBookingId,
        relatedInventoryId: payload.relatedInventoryId,
        relatedTravelerId: payload.relatedTravelerId,
        relatedDocumentId: payload.relatedDocumentId,
        metadata: payload.metadata,
        createdAt: payload.timestamp || new Date().toISOString(),
        updatedAt: payload.timestamp || new Date().toISOString(),
      };

      this.addNotification(notification);
    },

    /**
     * Mark notification as read
     */
    async markAsRead(notificationId: string) {
      try {
        await $fetch(`/api/notifications/${notificationId}/read`, {
          method: "PATCH",
        });

        const notification = this.notifications.find(
          (n) => n.id === notificationId
        );
        if (notification && notification.status === "UNREAD") {
          notification.status = "READ";
          notification.readAt = new Date().toISOString();
          // unreadCount is now calculated dynamically from getter
        }
      } catch (error) {
        logger.error("Error marking notification as read:", error);
      }
    },

    /**
     * Mark all as read
     */
    async markAllAsRead() {
      try {
        await $fetch("/api/notifications/read-all", {
          method: "PATCH",
        });

        this.notifications.forEach((n) => {
          if (n.status === "UNREAD") {
            n.status = "READ";
            n.readAt = new Date().toISOString();
          }
        });
        // unreadCount is now calculated dynamically from getter
      } catch (error) {
        logger.error("Error marking all as read:", error);
      }
    },

    /**
     * Archive notification
     */
    async archiveNotification(notificationId: string) {
      try {
        await $fetch(`/api/notifications/${notificationId}/archive`, {
          method: "PATCH",
        });

        const index = this.notifications.findIndex(
          (n) => n.id === notificationId
        );
        if (index !== -1) {
          // unreadCount is now calculated dynamically from getter
          this.notifications.splice(index, 1);
        }
      } catch (error) {
        logger.error("Error archiving notification:", error);
      }
    },

    /**
     * Play notification sound
     */
    playNotificationSound() {
      logger.log("[Notification] Playing notification sound...");
      try {
        // Create new audio instance each time to allow overlapping sounds
        const audio = new Audio("/sounds/notification.mp3");
        audio.volume = 0.5;

        // Play the sound
        const playPromise = audio.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              logger.log("[Notification] Sound played successfully");
            })
            .catch((err) => {
              // Common autoplay policy error
              if (err.name === "NotAllowedError") {
                logger.warn(
                  "[Notification] Sound blocked by browser autoplay policy. User interaction required."
                );
              } else {
                logger.error(
                  "[Notification] Could not play notification sound:",
                  err
                );
              }
            });
        }
      } catch (error) {
        logger.error(
          "[Notification] Error playing notification sound:",
          error
        );
      }
    },

    /**
     * Show browser notification
     */
    showBrowserNotification(notification: Notification) {
      if (!("Notification" in window)) {
        logger.log("[Notification] Browser notifications not supported");
        return;
      }

      if (Notification.permission !== "granted") {
        logger.log(
          "[Notification] Permission not granted:",
          Notification.permission
        );
        return;
      }

      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          tag: notification.id,
          requireInteraction: notification.priority === "URGENT",
          silent: false, // Allow sound
        });

        // Vibrate on mobile devices
        if (navigator.vibrate && notification.priority === "URGENT") {
          navigator.vibrate([200, 100, 200]);
        }

        browserNotification.onclick = () => {
          if (notification.actionUrl) {
            window.focus();
            navigateTo(notification.actionUrl);
          }
          browserNotification.close();
        };

        logger.log(
          "[Notification] Browser notification shown:",
          notification.title
        );
      } catch (error) {
        logger.error("Error showing browser notification:", error);
      }
    },

    /**
     * Clear all notifications
     */
    clearAll() {
      this.notifications = [];
      // unreadCount is now calculated dynamically from getter
    },

    /**
     * Reset store
     */
    reset() {
      this.notifications = [];
      // unreadCount is now calculated dynamically from getter
      this.loading = false;
      this.initialized = false;
    },
  },
});
